import { buildHoldbackLedger, drawApprovedAmount, drawWithheldAmount, effectiveDrawInvoices, sowRemainingAfterRequest, type HoldbackFlow } from "./financial.ts";
import type { AppData, Client, CompanyProfile, Draw, DrawInvoice, Job, SowItem } from "@/lib/types";

const COLUMNS = [
  "client_name", "company", "client_email", "client_phone", "client_address", "project_address", "project_name", "project_status",
  "sow_category", "sow_description", "contract_amount", "approved_budget_changes_overages", "adjusted_sow_budget",
  "draw_amount", "draw_status", "draw_notes", "estimated_arrival_date", "payment_received_at", "draw_created_at",
  "request_id", "invoice_number", "invoice_status", "draw_approved_amount", "draw_withheld_amount", "holdback_percent", "holdback_reason",
  "prior_holdback_available", "prior_holdback_applied", "prior_holdback_sources", "holdback_balance_after",
  "sow_remaining_balance", "invoice_requested_subtotal", "invoice_gc_fee_amount", "invoice_requested_total", "invoice_approved_total", "invoice_withheld_total", "remaining_contract_balance",
] as const;

type ExportRow = Record<(typeof COLUMNS)[number], string | number>;

function blankRow(): ExportRow {
  return Object.fromEntries(COLUMNS.map((column) => [column, ""])) as ExportRow;
}

function csvCell(value: string | number) {
  const normalized = String(value ?? "").replace(/\r\n?/g, "\n");
  return `"${normalized.replaceAll('"', '""')}"`;
}

function invoiceStatusLabel(status: DrawInvoice["status"]) {
  if (status === "PENDING_CLIENT_APPROVAL") return "Pending Client Approval";
  if (status === "PARTIALLY_PAID") return "Partially Paid";
  if (status === "CHANGES_REQUESTED") return "Denied — Changes Requested";
  if (status === "PAID") return "Paid";
  return "Approved";
}

function sowDecisionLabel(draw: Draw) {
  if (draw.status === "CLIENT_REVIEW") return "Pending Homeowner Decision";
  if (draw.status === "CHANGES_REQUESTED") return "Declined / Changes Requested";
  if (draw.status === "RECEIVED") return Number(draw.holdbackPercent || 0) > 0 ? `Approved with ${draw.holdbackPercent}% Holdback · Paid` : "Approved in Full · Paid";
  return Number(draw.holdbackPercent || 0) > 0 ? `Approved with ${draw.holdbackPercent}% Holdback` : "Approved in Full";
}

function sourceSummary(flow?: HoldbackFlow) {
  return flow?.priorHoldbackSources.map((source) => `${source.requestNumber} (${source.holdbackPercent}%): ${source.amount.toFixed(2)}`).join("; ") || "";
}

function drawValues(data: AppData, draw?: Draw, invoice?: DrawInvoice, flow?: HoldbackFlow): Partial<ExportRow> {
  if (!draw) return {};
  const requestId = draw.requestId || draw.id;
  return {
    draw_amount: draw.amount,
    draw_status: draw.status,
    draw_notes: draw.explanation,
    estimated_arrival_date: draw.estimatedArrivalDate || "",
    payment_received_at: draw.receivedAt || "",
    draw_created_at: draw.createdAt,
    request_id: requestId,
    invoice_number: invoice?.requestNumber || draw.requestNumber || `LEGACY-${requestId.slice(0, 8).toUpperCase()}`,
    invoice_status: invoice ? invoiceStatusLabel(invoice.status) : "",
    draw_approved_amount: drawApprovedAmount(draw),
    draw_withheld_amount: drawWithheldAmount(draw),
    holdback_percent: draw.holdbackPercent ?? 0,
    holdback_reason: draw.holdbackReason || "",
    prior_holdback_available: flow?.priorHoldbackAvailable || 0,
    prior_holdback_applied: flow?.priorHoldbackApplied || 0,
    prior_holdback_sources: sourceSummary(flow),
    holdback_balance_after: flow?.holdbackBalanceAfter || 0,
    sow_remaining_balance: sowRemainingAfterRequest(data, draw.sowId, requestId),
    invoice_requested_subtotal: invoice?.requestedSubtotal ?? draw.amount,
    invoice_gc_fee_amount: invoice?.gcFeeAmount ?? 0,
    invoice_requested_total: invoice?.requestedTotal ?? draw.amount,
    invoice_approved_total: invoice?.approvedTotal ?? drawApprovedAmount(draw),
    invoice_withheld_total: invoice?.withheldTotal ?? drawWithheldAmount(draw),
    remaining_contract_balance: invoice?.remainingContractBalance ?? "",
  };
}

function sowValues(sow?: SowItem): Partial<ExportRow> {
  if (!sow) return {};
  return {
    sow_category: sow.title,
    sow_description: sow.description,
    contract_amount: sow.contractAmount,
    approved_budget_changes_overages: sow.approvedBudgetChanges ?? 0,
    adjusted_sow_budget: sow.contractAmount + (sow.approvedBudgetChanges ?? 0),
  };
}

function jobValues(job?: Job): Partial<ExportRow> {
  if (!job) return {};
  return { project_address: job.address, project_name: job.title, project_status: job.status };
}

export function buildAquaDrawExportRows(data: AppData): ExportRow[] {
  const rows: ExportRow[] = [];
  const invoices = new Map(effectiveDrawInvoices(data).map((invoice) => [invoice.requestId, invoice]));
  const holdbackLedger = buildHoldbackLedger(data);
  for (const client of data.clients) {
    const clientValues: Partial<ExportRow> = {
      client_name: client.name,
      company: client.company || "",
      client_email: client.email,
      client_phone: client.phone,
      client_address: client.address,
    };
    const jobs = data.jobs.filter((job) => job.clientId === client.id);
    if (!jobs.length) { rows.push({ ...blankRow(), ...clientValues }); continue; }
    for (const job of jobs) {
      const sows = data.sowItems.filter((sow) => sow.jobId === job.id);
      if (!sows.length) { rows.push({ ...blankRow(), ...clientValues, ...jobValues(job) }); continue; }
      for (const sow of sows) {
        const draws = data.draws.filter((draw) => draw.sowId === sow.id);
        if (!draws.length) { rows.push({ ...blankRow(), ...clientValues, ...jobValues(job), ...sowValues(sow) }); continue; }
        for (const draw of draws) {
          const invoice = invoices.get(draw.requestId || draw.id);
          rows.push({ ...blankRow(), ...clientValues, ...jobValues(job), ...sowValues(sow), ...drawValues(data, draw, invoice, holdbackLedger.get(draw.id)) });
        }
      }
    }
  }
  return rows;
}

export function buildAquaDrawCsv(data: AppData) {
  const rows = buildAquaDrawExportRows(data);
  const lines = [COLUMNS.map(csvCell).join(","), ...rows.map((row) => COLUMNS.map((column) => csvCell(row[column])).join(","))];
  return { csv: `\uFEFF${lines.join("\r\n")}\r\n`, rowCount: rows.length };
}

type RequestStatementRow = {
  company_identity: string;
  client_name: string;
  client_company: string;
  client_address: string;
  project_name: string;
  project_address: string;
  invoice_label: string;
  invoice_date: string;
  invoice_number: string;
  request_id: string;
  sow_category: string;
  requested_notes: string;
  sow_decision: string;
  requested_amount: number;
  approved_amount: number;
  withheld_amount: number;
  prior_holdback_available: number;
  prior_holdback_applied: number;
  prior_holdback_sources: string;
  holdback_balance_after: number;
  remaining_sow_balance: number;
  invoice_subtotal: number;
  gc_fee_amount: number;
  invoice_total: number;
  approved_total: number;
  withheld_total: number;
  holdback_percent: number;
  holdback_reason: string;
  remaining_contract_balance: number;
  invoice_status: string;
};

const REQUEST_COLUMNS = [
  "company_identity", "client_name", "client_company", "client_address", "project_name", "project_address", "invoice_label", "invoice_date", "invoice_number", "request_id",
  "sow_category", "requested_notes", "sow_decision", "requested_amount", "approved_amount", "withheld_amount", "prior_holdback_available", "prior_holdback_applied", "prior_holdback_sources", "holdback_balance_after", "remaining_sow_balance",
  "invoice_subtotal", "gc_fee_amount", "invoice_total", "approved_total", "withheld_total", "holdback_percent", "holdback_reason", "remaining_contract_balance", "invoice_status",
] as const;

export function buildClientRequestStatementRows(input: { data: AppData; client: Client; jobs: Job[]; requestId?: string }) {
  const jobIds = new Set(input.jobs.map((job) => job.id));
  const jobsById = new Map(input.jobs.map((job) => [job.id, job]));
  const sowsById = new Map(input.data.sowItems.filter((sow) => jobIds.has(sow.jobId)).map((sow) => [sow.id, sow]));
  const invoices = effectiveDrawInvoices(input.data)
    .filter((invoice) => invoice.clientId === input.client.id && jobIds.has(invoice.jobId))
    .filter((invoice) => !input.requestId || invoice.requestId === input.requestId)
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.requestId.localeCompare(right.requestId));
  const rows: RequestStatementRow[] = [];
  const holdbackLedger = buildHoldbackLedger(input.data);

  invoices.forEach((invoice, index) => {
    const job = jobsById.get(invoice.jobId);
    if (!job) return;
    const requestDraws = input.data.draws
      .filter((draw) => (draw.requestId || draw.id) === invoice.requestId && sowsById.has(draw.sowId))
      .sort((left, right) => left.id.localeCompare(right.id));
    for (const draw of requestDraws) {
      const flow = holdbackLedger.get(draw.id);
      rows.push({
        company_identity: "Aqua Draw / Aqua Software Inc.",
        client_name: input.client.name,
        client_company: input.client.company || "",
        client_address: input.client.address || "",
        project_name: job.title,
        project_address: job.address,
        invoice_label: `Invoice ${index + 1}`,
        invoice_date: invoice.createdAt,
        invoice_number: invoice.requestNumber,
        request_id: invoice.requestId,
        sow_category: sowsById.get(draw.sowId)?.title || "SOW category",
        requested_notes: draw.explanation,
        sow_decision: sowDecisionLabel(draw),
        requested_amount: draw.amount,
        approved_amount: drawApprovedAmount(draw),
        withheld_amount: drawWithheldAmount(draw),
        prior_holdback_available: flow?.priorHoldbackAvailable || 0,
        prior_holdback_applied: flow?.priorHoldbackApplied || 0,
        prior_holdback_sources: sourceSummary(flow),
        holdback_balance_after: flow?.holdbackBalanceAfter || 0,
        remaining_sow_balance: sowRemainingAfterRequest(input.data, draw.sowId, invoice.requestId),
        invoice_subtotal: invoice.requestedSubtotal,
        gc_fee_amount: invoice.gcFeeAmount,
        invoice_total: invoice.requestedTotal,
        approved_total: invoice.approvedTotal,
        withheld_total: invoice.withheldTotal,
        holdback_percent: Number(draw.holdbackPercent || 0),
        holdback_reason: draw.holdbackReason || "",
        remaining_contract_balance: invoice.remainingContractBalance,
        invoice_status: invoiceStatusLabel(invoice.status),
      });
    }
  });
  return rows;
}

export function buildClientRequestStatementsCsv(input: { data: AppData; client: Client; jobs: Job[]; requestId?: string }) {
  const rows = buildClientRequestStatementRows(input);
  const lines = [REQUEST_COLUMNS.map(csvCell).join(","), ...rows.map((row) => REQUEST_COLUMNS.map((column) => csvCell(row[column])).join(","))];
  return { csv: `\uFEFF${lines.join("\r\n")}\r\n`, rowCount: rows.length };
}

function safeFilePart(value: string) {
  return value.normalize("NFKD").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 48) || "export";
}

async function shareOrDownload(file: File, title: string, text: string, rowCount: number) {
  const shareData = { title, text, files: [file] };
  if (typeof navigator.share === "function" && (!navigator.canShare || navigator.canShare(shareData))) {
    try {
      await navigator.share(shareData);
      return { mode: "shared" as const, fileName: file.name, rowCount };
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return { mode: "cancelled" as const, fileName: file.name, rowCount };
    }
  }
  const url = URL.createObjectURL(file);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = file.name;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { mode: "downloaded" as const, fileName: file.name, rowCount };
}

export async function exportAquaDrawData(input: { data: AppData; portalMode: boolean; clientName?: string }) {
  const { csv, rowCount } = buildAquaDrawCsv(input.data);
  const date = new Date().toISOString().slice(0, 10);
  const scope = input.portalMode ? safeFilePart(input.clientName || "client") : "workspace";
  const file = new File([csv], `Aqua-Draw-${scope}-project-summary-${date}.csv`, { type: "text/csv;charset=utf-8" });
  return shareOrDownload(file, "Aqua Draw Project Summary Export", "Aqua Draw project information export", rowCount);
}

function html(value: string | number) {
  return String(value ?? "").replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
}

const formatMoney = (value: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

function absoluteLogoUrl(logoUrl: string, portalUrl: string) {
  try { return new URL(logoUrl, portalUrl).href; } catch { return new URL("/aqua-homes-watermark.png", portalUrl).href; }
}

export type ClientDatedRequestRow = {
  companyIdentity: string;
  clientName: string;
  projectName: string;
  projectAddress: string;
  requestDate: string;
  requestNumber: string;
  requestId: string;
  sowCategory: string;
  requestedAmount: number;
  constructionSubtotal: number;
  gcFeeAmount: number;
  requestTotal: number;
  remainingContractBalance: number;
  requestStatus: string;
};

function requestStatusLabel(status: DrawInvoice["status"]) {
  if (status === "PENDING_CLIENT_APPROVAL") return "Request submitted — Awaiting client decision";
  if (status === "CHANGES_REQUESTED") return "Changes requested — Payment not recorded";
  if (status === "APPROVED") return "Approved — Payment not recorded";
  if (status === "PARTIALLY_PAID") return "Approved — Partially paid";
  return "Approved — Paid";
}

export function buildClientDatedRequestRows(input: { data: AppData; client: Client; jobs: Job[] }) {
  const clientJobs = input.jobs.filter((job) => job.clientId === input.client.id);
  const jobIds = new Set(clientJobs.map((job) => job.id));
  const jobsById = new Map(clientJobs.map((job) => [job.id, job]));
  const clientSows = input.data.sowItems.filter((sow) => jobIds.has(sow.jobId));
  const sowsById = new Map(clientSows.map((sow) => [sow.id, sow]));
  const sowOrder = new Map(clientSows.map((sow, index) => [sow.id, index]));
  const invoices = effectiveDrawInvoices(input.data)
    .filter((invoice) => invoice.clientId === input.client.id && jobIds.has(invoice.jobId))
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.requestId.localeCompare(right.requestId));
  const rows: ClientDatedRequestRow[] = [];

  for (const invoice of invoices) {
    const job = jobsById.get(invoice.jobId);
    if (!job) continue;
    const requestDraws = input.data.draws
      .filter((draw) => {
        const sow = sowsById.get(draw.sowId);
        return (draw.requestId || draw.id) === invoice.requestId && sow?.jobId === invoice.jobId;
      })
      .sort((left, right) => (sowOrder.get(left.sowId) ?? 0) - (sowOrder.get(right.sowId) ?? 0) || left.id.localeCompare(right.id));
    for (const draw of requestDraws) {
      rows.push({
        companyIdentity: "AquaDraw / Aqua Software Inc.",
        clientName: input.client.name,
        projectName: job.title,
        projectAddress: job.address,
        requestDate: invoice.createdAt,
        requestNumber: invoice.requestNumber,
        requestId: invoice.requestId,
        sowCategory: sowsById.get(draw.sowId)?.title || "SOW category",
        requestedAmount: draw.amount,
        constructionSubtotal: invoice.requestedSubtotal,
        gcFeeAmount: invoice.gcFeeAmount,
        requestTotal: invoice.requestedTotal,
        remainingContractBalance: invoice.remainingContractBalance,
        requestStatus: requestStatusLabel(invoice.status),
      });
    }
  }
  return rows;
}

export function buildClientDatedRequestsHtml(input: { data: AppData; client: Client; jobs: Job[]; companyProfile: CompanyProfile | null; portalUrl: string }) {
  const rows = buildClientDatedRequestRows(input);
  const groups = new Map<string, ClientDatedRequestRow[]>();
  for (const row of rows) groups.set(row.requestId, [...(groups.get(row.requestId) || []), row]);
  const logoUrl = absoluteLogoUrl(input.companyProfile?.logoUrl || "/aqua-homes-watermark.png", input.portalUrl);
  const statements = [...groups.values()].map((lines) => {
    const first = lines[0];
    const lineRows = lines.map((line) => `<tr><td>${html(line.sowCategory)}</td><td>${html(formatMoney(line.requestedAmount))}</td></tr>`).join("");
    const gcFeeRow = `<tr class="gc-fee-line"><td><strong>GC Fee</strong></td><td><strong>${html(formatMoney(first.gcFeeAmount))}</strong></td></tr>`;
    return `<section class="request-statement"><header><img src="${html(logoUrl)}" alt="AquaDraw logo"><div><strong>${html(first.companyIdentity)}</strong><h1>DRAW REQUEST STATEMENT</h1></div></header><div class="request-meta"><div><span>Client</span><strong>${html(first.clientName)}</strong></div><div><span>Project</span><strong>${html(first.projectName)}</strong></div><div><span>Project Address</span><strong>${html(first.projectAddress)}</strong></div><div><span>Request Date</span><strong>${html(new Date(first.requestDate).toLocaleString())}</strong></div><div><span>Request Number</span><strong>${html(first.requestNumber)}</strong></div><div><span>Stable Request ID</span><strong class="request-id">${html(first.requestId)}</strong></div><div class="status"><span>Request / Payment Status</span><strong>${html(first.requestStatus)}</strong></div></div><table><thead><tr><th>SOW Category</th><th>Requested Amount</th></tr></thead><tbody>${lineRows}${gcFeeRow}</tbody></table><footer><div><span>Construction Work</span><strong>${html(formatMoney(first.constructionSubtotal))}</strong></div><div><span>GC Fee</span><strong>${html(formatMoney(first.gcFeeAmount))}</strong></div><div><span>Invoice Total</span><strong>${html(formatMoney(first.requestTotal))}</strong></div><div><span>Remaining Construction Balance</span><strong>${html(formatMoney(first.remainingContractBalance))}</strong></div></footer><p class="notice">This dated statement summarizes one submitted draw request. It is not a full accounting invoice and does not process or record a payment.</p></section>`;
  }).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=5,user-scalable=yes"><title>AquaDraw Requests by Date</title><style>*{box-sizing:border-box}body{margin:0;background:#e9eef2;color:#142331;font-family:Arial,sans-serif}.toolbar{position:sticky;top:0;z-index:2;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 18px;background:#07141d;color:#fff}.toolbar button{min-height:42px;border:1px solid #d7b858;border-radius:9px;background:#153241;color:#fff;padding:9px 15px;font-weight:700}.statements{display:grid;gap:18px;padding:18px}.request-statement{width:min(8.5in,100%);min-height:10in;margin:0 auto;background:#fff;padding:.5in;box-shadow:0 3px 16px #1b2d3c40;break-after:page}.request-statement:last-child{break-after:auto}.request-statement header{display:flex;align-items:center;gap:14px;border-bottom:2px solid #17384a;padding-bottom:14px}.request-statement header img{width:58px;height:58px;object-fit:contain}.request-statement header strong{color:#17384a}.request-statement h1{margin:3px 0 0;color:#0f3554;font-size:22px;letter-spacing:.08em}.request-meta{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px 18px;margin:18px 0}.request-meta div{display:grid;gap:3px;min-width:0}.request-meta span,footer span{color:#5b6874;font-size:12px;text-transform:uppercase;letter-spacing:.04em}.request-meta strong{overflow-wrap:anywhere}.request-meta .status{grid-column:1/-1}table{width:100%;border-collapse:collapse;table-layout:fixed}th,td{padding:11px;border-bottom:1px solid #d5dee5;text-align:left}th{background:#edf3f6;color:#233b4c}th:last-child,td:last-child{text-align:right;white-space:nowrap}.gc-fee-line td{border-top:2px solid #b28b2f;background:#fff9e8;color:#49380e}footer{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;margin-top:20px}footer div{display:grid;gap:5px;border-top:2px solid #17384a;padding-top:10px}footer strong{font-size:18px}.notice{margin-top:20px;color:#536473;font-size:12px;line-height:1.5}.request-id{font-family:monospace;font-size:12px}@media(max-width:620px){.statements{padding:8px}.request-statement{min-height:0;padding:20px}.request-meta,footer{grid-template-columns:1fr}.request-meta .status{grid-column:auto}.request-statement h1{font-size:18px}}@page{size:letter portrait;margin:.45in}@media print{body{background:#fff}.toolbar{display:none}.statements{display:block;padding:0}.request-statement{width:auto;min-height:0;margin:0;padding:0;box-shadow:none}}</style></head><body><nav class="toolbar"><strong>AquaDraw Requests by Date</strong><button type="button" onclick="window.print()">Print / Save PDF</button></nav><main class="statements">${statements || "<p>No submitted draw requests are available.</p>"}</main></body></html>`;
}

export async function exportClientDatedRequests(input: { data: AppData; client: Client; jobs: Job[]; companyProfile: CompanyProfile | null }) {
  const rows = buildClientDatedRequestRows(input);
  const requestCount = new Set(rows.map((row) => row.requestId)).size;
  if (!requestCount) throw new Error("No submitted draw requests are available to export.");
  const date = new Date().toISOString().slice(0, 10);
  const htmlDocument = buildClientDatedRequestsHtml({ ...input, portalUrl: window.location.origin });
  const file = new File([htmlDocument], `AquaDraw-${safeFilePart(input.client.name)}-requests-by-date-${date}.html`, { type: "text/html;charset=utf-8" });
  return shareOrDownload(file, "AquaDraw Requests by Date", "Dated draw request statements", requestCount);
}

export function buildClientRequestStatementsHtml(input: { data: AppData; client: Client; jobs: Job[]; companyProfile: CompanyProfile; portalUrl: string; returnUrl?: string; requestId?: string }) {
  const rows = buildClientRequestStatementRows(input).filter((row) => !input.requestId || row.request_id === input.requestId);
  const groups = new Map<string, RequestStatementRow[]>();
  for (const row of rows) groups.set(row.request_id, [...(groups.get(row.request_id) || []), row]);
  const profile = input.companyProfile;
  const logoUrl = absoluteLogoUrl(profile.logoUrl, input.portalUrl);
  const invoices = [...groups.values()].map((lines) => {
    const first = lines[0];
    const billTo = [first.client_name, first.client_company, first.client_address].filter(Boolean).map(html).join("<br>");
    const lineRows = lines.map((line) => {
      const carryIn = line.prior_holdback_applied > 0 ? `<small>Prior holdback returned: ${html(line.prior_holdback_sources)}. Included in this request, not added to it.</small>` : "";
      const decision = `<small><b>Homeowner decision:</b> ${html(line.sow_decision)}${line.holdback_reason ? ` · ${html(line.holdback_reason)}` : ""}</small>`;
      return `<tr><td><strong>${html(line.sow_category)}</strong><small>${html(line.requested_notes)}</small>${decision}${carryIn}</td><td class="money">${html(formatMoney(line.requested_amount))}</td><td class="money">${html(formatMoney(line.prior_holdback_applied))}</td><td class="money">${html(formatMoney(line.withheld_amount))}</td><td class="money">${html(formatMoney(line.approved_amount))}</td><td class="money">${html(formatMoney(line.holdback_balance_after))}</td><td class="money">${html(formatMoney(line.remaining_sow_balance))}</td></tr>`;
    }).join("");
    const priorCarryIn = lines.reduce((sum, line) => sum + line.prior_holdback_applied, 0);
    const carryforwardAfter = lines.reduce((sum, line) => sum + line.holdback_balance_after, 0);
    const gcFeeRow = `<tr class="gc-fee-line" style="background:#fff9e8;color:#49380e"><td style="border-top:2px solid #b28b2f"><strong>GC Fee</strong><small>Calculated once from this invoice’s construction work.</small></td><td class="money" style="border-top:2px solid #b28b2f"><strong>${html(formatMoney(first.gc_fee_amount))}</strong></td><td class="money" style="border-top:2px solid #b28b2f">—</td><td class="money" style="border-top:2px solid #b28b2f">—</td><td class="money" style="border-top:2px solid #b28b2f">Included below</td><td class="money" style="border-top:2px solid #b28b2f">—</td><td class="money" style="border-top:2px solid #b28b2f">—</td></tr>`;
    return `<div class="paper-frame"><section class="invoice"><span class="page-size-label">US Letter · Landscape · 11 × 8.5 in</span><header class="invoice-header"><img src="${html(logoUrl)}" alt="${html(profile.displayName)} logo"><div class="company-copy"><h1>${html(profile.displayName)}</h1><p>${html(profile.legalName)}<br>${html(profile.address)}<br>${html(profile.phone)} · ${html(profile.email)}<br>${html(profile.website)}</p></div><div class="invoice-meta"><strong>INVOICE</strong><p>${html(first.invoice_label)}<br>Invoice #: ${html(first.invoice_number)}<br>Invoice Date: ${html(new Date(first.invoice_date).toLocaleString())}<br>Invoice Status: ${html(first.invoice_status)}</p></div></header><hr><section class="project"><strong>Bill To</strong><span>${billTo}</span><strong>Project</strong><span>${html(first.project_name)}</span><strong>Project Address</strong><span>${html(first.project_address)}</span><strong>Client PWA</strong><span>${html(input.portalUrl)}</span></section><table><colgroup><col class="description-column"><col span="6" class="money-column"></colgroup><thead><tr><th>SOW Line / Description</th><th>Requested</th><th>Prior Holdback In</th><th>Held Now</th><th>Net Approved</th><th>Holdback Remaining</th><th>SOW Remaining</th></tr></thead><tbody>${lineRows}${gcFeeRow}</tbody></table><footer><p>Construction Work <b>${html(formatMoney(first.invoice_subtotal))}</b></p><p>GC Fee <b>${html(formatMoney(first.gc_fee_amount))}</b></p><p>Invoice Total <b>${html(formatMoney(first.invoice_total))}</b></p><p>Prior Holdback Included <b>${html(formatMoney(priorCarryIn))}</b></p><p>SOW Decisions <b>See each line</b></p><p>Dollars Withheld Now <b>${html(formatMoney(first.withheld_total))}</b></p><p>Net Approved <b>${html(formatMoney(first.approved_total))}</b></p><p>Holdback Available After This Invoice <b>${html(formatMoney(carryforwardAfter))}</b></p><p>Remaining Construction Balance <b>${html(formatMoney(first.remaining_contract_balance))}</b></p><small class="carry-note">Prior holdback is already included in Construction Work and never increases this invoice.</small></footer></section></div>`;
  }).join("");
  const exitUrl = (() => {
    try {
      const portal = new URL(input.portalUrl);
      const candidate = new URL(input.returnUrl || input.portalUrl, portal);
      return candidate.origin === portal.origin ? candidate.href : portal.href;
    } catch { return input.portalUrl; }
  })();
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=5,user-scalable=yes"><title>AquaDraw Invoice Print Preview</title><style>*{box-sizing:border-box}html{background:#e9eef2}body{font-family:Arial,sans-serif;background:#e9eef2;color:#17212b;margin:0}.preview-toolbar{position:sticky;top:0;z-index:10;display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center;gap:12px;padding:10px 12px;background:#0b1822;color:#fff;box-shadow:0 3px 14px #0004}.preview-exit,.print-control{display:inline-flex;align-items:center;justify-content:center;min-height:42px;border:1px solid #78ddea;background:#102b38;color:#fff;border-radius:8px;padding:9px 14px;font:700 14px Arial,sans-serif;text-decoration:none;cursor:pointer}.print-control{border-color:#e6bd55;background:#17384a}.preview-title{display:grid;gap:2px;text-align:center}.preview-title strong{font-size:14px}.preview-title small{color:#b8cbd6;font-size:11px}.preview-help{margin:10px 12px 0;text-align:center;color:#405466;font-size:12px}.preview-stage{display:grid;gap:18px;justify-items:center;overflow:auto;padding:12px;touch-action:pan-x pan-y pinch-zoom}.paper-frame{position:relative;margin:0 auto}.invoice{position:relative;width:11in;min-height:8.5in;margin:0;background:#fff;padding:.4in .45in .42in;box-shadow:0 3px 16px #1b2d3c40;transform-origin:top left}.page-size-label{position:absolute;right:.45in;bottom:.16in;color:#71808d;font-size:7pt;letter-spacing:.04em;text-transform:uppercase}.invoice-header{display:grid;grid-template-columns:.72in minmax(0,1fr) minmax(2.8in,3.6in);gap:.18in;align-items:start}.invoice-header img{width:.62in;height:.62in;object-fit:contain}.company-copy h1{margin:0;color:#0f3554;font-size:16pt;line-height:1.1}.company-copy p,.invoice-meta p{margin:4pt 0 0;font-size:8.5pt;line-height:1.3}.invoice-meta{text-align:right}.invoice-meta>strong{display:block;color:#0f3554;font-size:18pt;letter-spacing:.08em}.invoice hr{border:0;border-top:1px solid #aebbc5;margin:10pt 0}.project{display:grid;grid-template-columns:.76in minmax(1.3in,1.35fr) .92in minmax(2in,2fr);gap:5pt 10pt;margin:10pt 0 12pt;font-size:8.5pt;line-height:1.28}.project strong{color:#243746}.project span{min-width:0;overflow-wrap:anywhere}table{border-collapse:collapse;width:100%;table-layout:fixed;font-size:8.25pt;line-height:1.22}.description-column{width:31%}.money-column{width:11.5%}th,td{padding:5pt 4pt;border-bottom:1px solid #d9e1e8;text-align:left;vertical-align:top;overflow-wrap:break-word;word-break:normal;hyphens:auto}th{background:#edf3f6;color:#233b4c;font-size:7.5pt;font-weight:700}th:not(:first-child),td.money{text-align:right}td small{display:block;margin-top:3pt;color:#52606d}.money{white-space:nowrap}footer{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:7pt 10pt;margin-top:12pt;font-size:8pt}footer p{display:grid;gap:2pt;margin:0;padding-top:5pt;border-top:1px solid #cbd5dc;color:#52606d}footer p b{color:#17212b;font-size:9.5pt}.carry-note{grid-column:1/-1;color:#52606d}.reason{grid-column:1/-1;display:grid;grid-template-columns:1fr;gap:3pt;margin:0}.reason span{font-size:7.5pt;text-transform:uppercase;color:#52606d}@media(max-width:620px){.preview-toolbar{grid-template-columns:auto minmax(0,1fr)}.preview-title{text-align:left}.print-control{grid-column:1/-1}.preview-exit,.print-control{min-height:40px;padding:8px 10px;font-size:13px}}@page{size:letter landscape;margin:.45in}@media print{html,body{width:100%;background:#fff}.preview-toolbar,.preview-help,.page-size-label{display:none!important}.preview-stage{display:block;overflow:visible;padding:0}.paper-frame{display:contents;width:auto!important;height:auto!important}.invoice{width:auto!important;min-height:0!important;margin:0;padding:0;box-shadow:none;transform:none!important;break-after:page;page-break-after:always}.paper-frame:last-child .invoice{break-after:auto;page-break-after:auto}.invoice-header,.project,footer{break-inside:avoid;page-break-inside:avoid}thead{display:table-header-group}tfoot{display:table-footer-group}tr{break-inside:avoid;page-break-inside:avoid}}</style></head><body><nav class="preview-toolbar" aria-label="Invoice print preview controls"><a class="preview-exit" href="${html(exitUrl)}" onclick="window.close()">← Exit Print View</a><div class="preview-title"><strong>Invoice Print Preview</strong><small>US Letter · Landscape · 11 × 8.5 inches</small></div><button class="print-control" type="button" onclick="window.print()">Print / Save PDF</button></nav><p class="preview-help">Each white sheet is the paper boundary. The sheet is scaled only to fit this screen; printing stays full-size and longer tables continue onto additional sheets.</p><main class="preview-stage">${invoices || "<p>No submitted invoices are available for this project.</p>"}</main><script>(()=>{const pageWidth=1056;const pageHeight=816;const fitPages=()=>{const scale=Math.min(1,Math.max(.2,(window.innerWidth-24)/pageWidth));document.querySelectorAll(".paper-frame").forEach((frame)=>{const page=frame.querySelector(".invoice");if(!page)return;page.style.transform="scale("+scale+")";frame.style.width=Math.ceil(pageWidth*scale)+"px";frame.style.height=Math.ceil(Math.max(pageHeight,page.scrollHeight)*scale)+"px"})};window.addEventListener("resize",fitPages);window.addEventListener("load",fitPages);requestAnimationFrame(fitPages)})()</script></body></html>`;
}

export function openClientInvoice(input: { data: AppData; client: Client; jobs: Job[]; companyProfile: CompanyProfile; requestId?: string }) {
  const invoiceWindow = window.open("", "_blank");
  if (!invoiceWindow) return false;
  invoiceWindow.opener = null;
  invoiceWindow.document.open();
  invoiceWindow.document.write(buildClientRequestStatementsHtml({ ...input, portalUrl: window.location.origin, returnUrl: window.location.href }));
  invoiceWindow.document.close();
  return true;
}

export type InvoiceExportFormat = "print" | "html" | "csv" | "word";

export async function exportClientRequestStatements(input: {
  data: AppData;
  client: Client;
  jobs: Job[];
  companyProfile: CompanyProfile;
  requestId?: string;
  format?: InvoiceExportFormat;
}) {
  const required = [input.companyProfile.legalName, input.companyProfile.displayName, input.companyProfile.address, input.companyProfile.phone, input.companyProfile.email, input.companyProfile.website];
  if (required.some((value) => !value.trim())) throw new Error("Complete the company invoice information in Settings before exporting invoices.");
  const rows = buildClientRequestStatementRows(input);
  const invoiceCount = new Set(rows.map((row) => row.request_id)).size;
  if (!invoiceCount) throw new Error("No visible invoices are available to export.");

  const format = input.format || "html";
  if (format === "print") {
    if (!openClientInvoice(input)) throw new Error("Allow pop-ups for AquaDraw to print or save this invoice as a PDF.");
    return { mode: "opened" as const, fileName: "", rowCount: invoiceCount };
  }

  const date = new Date().toISOString().slice(0, 10);
  const invoiceName = input.requestId && rows[0]
    ? `invoice-${safeFilePart(rows[0].invoice_number)}`
    : `invoices-${date}`;
  const baseName = `AquaDraw-${safeFilePart(input.client.name)}-${invoiceName}`;

  if (format === "csv") {
    const { csv } = buildClientRequestStatementsCsv(input);
    const file = new File([csv], `${baseName}.csv`, { type: "text/csv;charset=utf-8" });
    return shareOrDownload(file, "AquaDraw Excel Export", "AquaDraw invoice data for Excel", invoiceCount);
  }

  const htmlDocument = buildClientRequestStatementsHtml({ ...input, portalUrl: window.location.origin });
  if (format === "word") {
    const wordDocument = htmlDocument.replace("<head>", '<head><meta name="ProgId" content="Word.Document">');
    const file = new File([wordDocument], `${baseName}.doc`, { type: "application/msword" });
    return shareOrDownload(file, "AquaDraw Word Export", "AquaDraw invoice document", invoiceCount);
  }

  const file = new File([htmlDocument], `${baseName}.html`, { type: "text/html;charset=utf-8" });
  return shareOrDownload(file, "AquaDraw Invoice File", "AquaDraw invoice export", invoiceCount);
}
